import { Navigate, useLocation } from 'react-router-dom';
import { ReactElement } from 'react';
import { useAuth } from '@/components/auth/AuthProvider';
import { useUserRole } from '@/hooks/useUserRole';

/**
 * SECURITY: Route guard using secure role checking
 * Uses user_roles table as source of truth
 */
export function RequireRole({ role, children }: { role: 'student'|'instructor'|'admin'; children: ReactElement }) {
  const { user, loading: authLoading } = useAuth();
  const { role: userRole, loading: roleLoading } = useUserRole();
  const location = useLocation();

  // SECURITY: never render protected children before auth AND role are resolved.
  if (authLoading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Checking your access…</p>
        </div>
      </div>
    );
  }


  // Redirect to login with next parameter if not authenticated
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?next=${next}`} replace />;
  }

  // Admins have full access; otherwise the role must match exactly
  if (userRole !== role && userRole !== 'admin') {
    return <Navigate to="/unauthorised" replace />;
  }
  
  return children;
}